import {
  Box,
  FormControl,
  FormLabel,
  Switch
} from "@chakra-ui/react";
import {
  format,
  isAfter,
  isBefore
} from "date-fns";
import {
  useCallback,
  useEffect,
  useRef,
  useState
} from "react";
import styled, { css } from "styled-components";

import {
  Conversation,
  Message
} from "src/api";
import { Text } from "src/components/text";
import { useAuthContext } from "src/hooks/useAuthContext";
import { Avatar } from "src/components/avatar";

interface MessagesProps {
  messages: Message[]
  conversation: Conversation;
}

export const Messages: React.FC<MessagesProps> = props => {
  const { messages, conversation } = props;
  const [ sort, setSort ] = useState<boolean>(false);
  const { authDataState } = useAuthContext();
  const myRef = useRef<HTMLDivElement>(null);

  const executeScroll = () => {
    myRef.current?.scrollTo({ top: myRef.current?.scrollHeight });
  };

  const isMessageAuthorCurrentUser = useCallback((message: Message) => {
    const author = conversation?.participants?.find(p => p.user._id === message.userId);

    if (author?.user._id === authDataState.user?._id) {
      return true;
    } else {
      return false;
    }
  }, [ conversation, authDataState ]);

  useEffect(() => {
    // Every time a new message comes through, scroll to bottom of messages window.
    executeScroll();
  }, [ messages ]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      height="100%"
    >
      <Box
        paddingBottom={5}
        display="flex"
        flexDirection="column"
        alignItems="flex-start"
        mt={10}
      >
        <Box pb="0.5rem">
          <Text as="p">
            Currently sorting by
            {" "}

            <strong>
              {!sort ? "time received." : "time sent."}
            </strong>
          </Text>
        </Box>

        <FormControl
          width="auto"
          display="flex"
          alignItems="center"
        >

          <Switch
            id="toggle-time"
            mr={4}
            onChange={e => setSort(e.target.checked)}
          />

          <FormLabel
            htmlFor="toggle-time"
            mb="0"
          >
            <Text as="p">
              Sort by time
              {" "}

              <strong>
                {sort ? "received" : "sent"}
              </strong>
            </Text>
          </FormLabel>
        </FormControl>
      </Box>

      <Box
        height="0"
        minHeight="200px"
        overflowY="scroll"
        flex="1 1 auto"
        display="flex"
        flexDirection="column"
        borderRadius={4}
        bg="whitesmoke"
        mb={5}
        ref={myRef}
      >

        {messages.sort((msg, prev) => {
          /**
           * If we aren't sorting messages, then use server time
           * If we are sorting messages, then use the sender time.
           * We already only ever use sender time for the current user's own messages.
           */
          const messageDate = new Date(isMessageAuthorCurrentUser(msg) ? msg.senderTimeCreated : sort ? msg.senderTimeCreated : msg.serverTimeCreated);
          const prevMessageDate = new Date(isMessageAuthorCurrentUser(prev) ? prev.senderTimeCreated : sort ? prev.senderTimeCreated : prev.serverTimeCreated);

          if (isAfter(messageDate, prevMessageDate)) return 1;

          if (isBefore(messageDate, prevMessageDate)) return -1;

          return 0;
        }).map((msg, i) => {
          const author = conversation?.participants?.find(p => p.user._id === msg.userId);
          let authorIsCurrentUser = false;

          if (author?.user._id === authDataState.user?._id) {
            authorIsCurrentUser = true;
          }

          return (
            <MessageContainer
              className={authorIsCurrentUser ? "byCurrentUser" : ""}
              p={2}
            // eslint-disable-next-line react/no-array-index-key
              key={`${msg}+${i}`}
            >
              <Box
                display="flex"
                dir="row"
                alignItems="center"
              >
                <Avatar
                  name={author?.user.name || ""}
                  hideName
                  size="xs"
                />

                <Text
                  style={{ marginLeft: "5px" }}
                  as="small"
                  bold
                >
                  {authorIsCurrentUser ? "You" : author?.user.name}
                </Text>

              </Box>

              <Box mb={2}>
                <Text
                  as="small"
                >
                  {authorIsCurrentUser ? (
                    `Sent ${format(new Date(msg.senderTimeCreated), "HH:mm:ss")}`
                  ) : sort ?
                    `Sent ${format(new Date(msg.senderTimeCreated), "HH:mm:ss")}` :
                    `Received ${format(new Date(msg.serverTimeCreated), "HH:mm:ss")}`}
                </Text>

              </Box>

              <Box className="message">
                <Text as="p">
                  {msg.content}
                </Text>
              </Box>
            </MessageContainer>
          );
        })}

      </Box>
    </Box>

  );
};

const MessageContainer = styled(Box)`
  ${({ theme }) => css`
    text-align: left;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
      
    .message {
      border: 2px solid ${theme.colors.main};
      display: flex;
      max-width: 80%;
      display: flex;
      padding: 10px;
      border-radius: 5px;
    }
    
    &.byCurrentUser {
      justify-content: flex-end;
      align-items: flex-end;
      
      .message {
        background: ${theme.colors.main};
      }
    }
  `}
`;